import type { ExpressRateBand } from "@/lib/express-rates";
import { DEFAULT_EXPRESS_RATE_SCHEDULE } from "@/lib/express-rates";

export type KycStatus = "not_started" | "pending" | "approved" | "declined";
/** express = entry (bank statement); verified = salary + NOK + guarantor; elite = earned / admin-granted */
export type VerificationLevel = "none" | "express" | "verified" | "elite";
export type LoanStatus =
  | "pending"
  | "approved"
  | "countered"
  | "counter_accepted"
  | "active"
  | "repaid"
  | "overdue"
  | "declined";

/** Loan tenor in days (1–30). */
export type LoanDurationDays = number;

export const MIN_LOAN_DAYS = 1;
export const MAX_LOAN_DAYS = 30;
/** @deprecated Prefer graceDaysForLevel — kept as fallback when loan has no tier. */
export const GRACE_DAYS = 2;

export const TIER_LABEL: Record<"express" | "verified" | "elite", string> = {
  express: "Express",
  verified: "Verified",
  elite: "Elite",
};

/** Grace days after due date, by eligibility level (King CC product rules). */
export function graceDaysForLevel(level: VerificationLevel | undefined | null): number {
  if (level === "express") return 1;
  if (level === "verified") return 3;
  if (level === "elite") return 5;
  return GRACE_DAYS;
}

/**
 * Max rollovers (carryovers) allowed.
 * `0` = none (Express — product rule; flip here if King re-enables Express rollover).
 * `null` = unlimited subject to approval (Elite).
 */
export function maxRolloversForLevel(level: VerificationLevel | undefined | null): number | null {
  if (level === "express") return 0;
  if (level === "verified") return 2;
  if (level === "elite") return null;
  return 2;
}

/** Caps / rates (admin can override per borrower after review). */
export const EXPRESS_DEFAULT_LIMIT = 100_000;
export const EXPRESS_FIXED_RATE = 10;
export const VERIFIED_DEFAULT_LIMIT = 500_000;
export const ELITE_DEFAULT_LIMIT = 10_000_000;

/** Elite-ready signal from in-app credit score (admin still grants Elite). */
export const ELITE_SCORE_THRESHOLD_100 = 75;
export const ELITE_SCORE_THRESHOLD_850 = 720;

/** @deprecated use EXPRESS_* */
export const CREST_DEFAULT_LIMIT = EXPRESS_DEFAULT_LIMIT;
/** @deprecated use EXPRESS_FIXED_RATE */
export const CREST_FIXED_RATE = EXPRESS_FIXED_RATE;
/** @deprecated use VERIFIED_DEFAULT_LIMIT */
export const SOVEREIGN_DEFAULT_LIMIT = VERIFIED_DEFAULT_LIMIT;

/** Express & Verified cap at 30 days; Elite can pick up to 1 year. */
export const ELITE_MIN_LOAN_DAYS = 1;
export const ELITE_MAX_LOAN_DAYS = 365;

export interface BorrowerTerms {
  /** Resolved Express weekly bands (platform default + optional per-borrower override). */
  expressRateSchedule: ExpressRateBand[];
  /** Per-borrower override stored in DB; null = use platform default only. */
  expressRateScheduleOverride?: ExpressRateBand[] | null;
  /** @deprecated legacy single rate — use expressRateSchedule */
  expressRate: number;
  /** Admin-only reference for Verified/Elite internal review */
  verifiedGuideRate: number;
  allowNegotiate: boolean;
  crestRate?: number;
  sovereignGuideRate?: number;
}

export const DEFAULT_TERMS: BorrowerTerms = {
  expressRateSchedule: DEFAULT_EXPRESS_RATE_SCHEDULE,
  expressRateScheduleOverride: null,
  expressRate: DEFAULT_EXPRESS_RATE_SCHEDULE[0].rate,
  verifiedGuideRate: 8,
  allowNegotiate: true,
  crestRate: DEFAULT_EXPRESS_RATE_SCHEDULE[0].rate,
  sovereignGuideRate: 8,
};

export interface KycDocument {
  type: string;
  fileName: string;
  storagePath: string;
}

export interface KycContextAnalysis {
  consistency_flags?: string[];
  positive_signals?: string[];
  questions_for_admin?: string[];
  salary_vs_limit_check?: string;
  nok_guarantor_notes?: string;
  overall_context_summary?: string;
  context_risk_level?: "low" | "medium" | "high" | string;
}

export interface Borrower {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  emailVerified: boolean;
  kycStatus: KycStatus;
  verificationLevel: VerificationLevel;
  kycId?: string;
  kyc?: {
    bvn?: string;
    nin?: string;
    dateOfBirth?: string;
    houseAddress?: string;
    officeAddress?: string;
    workplaceName?: string;
    salaryAmount?: number;
    loanPurpose?: string;
    repaymentPlan?: string;
    socialInstagram?: string;
    socialFacebook?: string;
    socialTwitter?: string;
    socialYoutube?: string;
    nokName?: string;
    nokRelationship?: string;
    nokPhone?: string;
    nokAddress?: string;
    guarantorName?: string;
    guarantorRelationship?: string;
    guarantorPhone?: string;
    guarantorAddress?: string;
    consentGiven?: boolean;
    consentGivenAt?: string;
    verificationLevel?: VerificationLevel;
    idFrontName?: string;
    idBackName?: string;
    selfieName?: string;
    statementName?: string;
    documents?: KycDocument[];
    aiSummary?: string;
    aiSuggestedLimit?: number;
    aiRiskScore?: number;
    aiAnalysisRaw?: unknown;
    aiKycContextSummary?: string;
    aiKycContextRaw?: KycContextAnalysis | unknown;
    declineReason?: string;
    submittedAt?: string;
  };
  loanLimit: number;
  creditScore: number;
  creditScoreUnlocked: boolean;
  terms: BorrowerTerms;
}

export interface Installment {
  id: string;
  loanId: string;
  dueDate: string;
  amount: number;
  paid: boolean;
  paidAt?: string;
  status?: string;
}

export interface Loan {
  id: string;
  borrowerId: string;
  /** 1–30 */
  durationDays: number;
  amount: number;
  interestRate: number;
  split: boolean;
  status: LoanStatus;
  createdAt: string;
  dueDate: string;
  paid: number;
  installments: Installment[];
  declineReason?: string;
  proposedRepay?: number;
  counterPrincipal?: number;
  counterRepay?: number;
  counterDurationDays?: number;
  counterDueDate?: string;
  counterRate?: number;
  counterNote?: string;
  graceEndsAt?: string;
  /** Tier at application / approval time */
  verificationLevel?: VerificationLevel;
  /** Grace days stored on the loan (defaults from verificationLevel) */
  graceDays?: number;
  /** How many rollovers already taken */
  rolloverCount?: number;
}

/** Quote for rolling over remaining balance into a new tenure. */
export interface RolloverQuote {
  loanId: string;
  /** Outstanding balance — becomes the new capital */
  remaining: number;
  durationDays: number;
  ratePercent: number;
  /** Interest that must be paid now to unlock the rollover */
  interestDue: number;
  /** New total repayable after rollover = remaining + interest for the new tenure */
  newRepayable: number;
  canNegotiate: boolean;
}

export function loanTotalDue(loan: Pick<Loan, "proposedRepay" | "amount" | "interestRate">): number {
  return loan.proposedRepay ?? loan.amount * (1 + loan.interestRate / 100);
}

export function loanOutstanding(loan: Pick<Loan, "proposedRepay" | "amount" | "interestRate" | "paid">): number {
  return Math.max(0, Math.round((loanTotalDue(loan) - loan.paid) * 100) / 100);
}

export interface NotificationRow {
  id: string;
  userId: string;
  body: string;
  createdAt: string;
  read: boolean;
}

export interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  bankCode: string;
  isPrimary: boolean;
  verified: boolean;
}

export const NOK_RELATIONSHIPS = ["Father", "Mother", "Spouse"] as const;

export const KYC_DOC_LABELS: Record<string, string> = {
  nin_front: "NIN Card (Front)",
  id_front: "ID Front",
  id_back: "ID Back",
  selfie: "Selfie",
  bank_statement: "Bank Statement",
  office_id: "Office ID",
  utility_bill: "Utility Bill",
  valid_id: "Valid ID",
  salary_evidence: "Salary Evidence",
  nok_id: "Next of Kin ID",
  guarantor_id: "Guarantor ID",
};

export const KYC_IMAGE_TYPES = new Set([
  "nin_front",
  "selfie",
  "office_id",
  "utility_bill",
  "nok_id",
  "guarantor_id",
  "id_front",
  "id_back",
  "valid_id",
]);

export const KYC_PDF_TYPES = new Set(["bank_statement", "salary_evidence"]);

/** Express required uploads (salary job proof is not required at Express). */
export const EXPRESS_DOC_TYPES = [
  "nin_front",
  "selfie",
  "utility_bill",
  "bank_statement",
] as const;

/** Optional at Express; required at Verified (salary job proof). */
export const EXPRESS_OPTIONAL_DOC_TYPES = [
  "office_id",
  "salary_evidence",
] as const;

/** Extra docs required for Verified (NOK + guarantor; bank statement already on Express). */
export const VERIFIED_EXTRA_DOC_TYPES = [
  "nok_id",
  "guarantor_id",
] as const;

/** @deprecated use EXPRESS_DOC_TYPES */
export const CREST_DOC_TYPES = [
  ...EXPRESS_DOC_TYPES,
  ...EXPRESS_OPTIONAL_DOC_TYPES,
] as const;

/** @deprecated use VERIFIED_EXTRA_DOC_TYPES */
export const SOVEREIGN_DOC_TYPES = [
  "nok_id",
  "guarantor_id",
] as const;

/** Normalize DB / legacy values (crest→express, sovereign→verified). */
export function normalizeVerificationLevel(v: unknown): VerificationLevel {
  if (v === "elite") return "elite";
  if (v === "verified" || v === "sovereign") return "verified";
  if (v === "express" || v === "crest") return "express";
  if (v === "none") return "none";
  return "none";
}

export function isVerificationLevel(v: unknown): boolean {
  return (
    v === "none" ||
    v === "express" ||
    v === "verified" ||
    v === "elite" ||
    v === "crest" ||
    v === "sovereign"
  );
}

/**
 * Express (or unset) member whose KYC is pending for Verified — admin decision required;
 * borrower must not re-apply until approved or declined.
 */
export function isVerifiedUpgradePending(
  b: Pick<Borrower, "kycStatus" | "verificationLevel" | "kyc">,
): boolean {
  if (b.kycStatus !== "pending") return false;
  const requested = b.kyc?.verificationLevel;
  if (requested !== "verified" && requested !== "elite") return false;
  return b.verificationLevel === "express" || b.verificationLevel === "none";
}

/** True when Verified member looks Elite-ready from unlocked credit score (admin still grants). */
export function isEliteReady(b: Pick<Borrower, "verificationLevel" | "creditScore" | "creditScoreUnlocked">): boolean {
  if (b.verificationLevel !== "verified") return false;
  if (!b.creditScoreUnlocked) return false;
  const s = b.creditScore;
  if (s <= 100) return s >= ELITE_SCORE_THRESHOLD_100;
  return s >= ELITE_SCORE_THRESHOLD_850;
}

export function canNegotiateRates(level: VerificationLevel): boolean {
  return level === "verified" || level === "elite";
}

export function maxLoanDaysForLevel(level: VerificationLevel): number {
  if (level === "elite") return ELITE_MAX_LOAN_DAYS;
  return MAX_LOAN_DAYS;
}

export function clampLoanDaysForLevel(level: VerificationLevel, n: number): number {
  const rounded = Math.max(MIN_LOAN_DAYS, Math.round(n));
  return Math.min(maxLoanDaysForLevel(level), rounded);
}

export function clampLoanDays(n: number): number {
  if (!Number.isFinite(n)) return MAX_LOAN_DAYS;
  return Math.min(MAX_LOAN_DAYS, Math.max(MIN_LOAN_DAYS, Math.round(n)));
}

export function formatDurationDays(days: number, opts?: { capAt30?: boolean }): string {
  const d = opts?.capAt30 === false
    ? Math.max(MIN_LOAN_DAYS, Math.round(days))
    : clampLoanDays(days);
  return d === 1 ? "1 day" : `${d} days`;
}

/** @deprecated kept for legacy row mapping */
export type LoanDuration = "14d" | "1m" | "3m" | "6m" | `${number}d`;

export const DURATION_LABEL: Record<string, string> = {
  "14d": "14 days",
  "1m": "1 month",
  "3m": "3 months",
  "6m": "6 months",
};

export const DURATION_DAYS: Record<string, number> = {
  "14d": 14,
  "1m": 30,
  "3m": 90,
  "6m": 180,
};

export const DURATIONS = ["14d", "1m", "3m", "6m"] as const;

export function isLoanDuration(v: unknown): v is LoanDuration {
  return typeof v === "string" && (DURATIONS as readonly string[]).includes(v);
}

export function daysFromLoanRow(duration: unknown, durationDays?: unknown): number {
  if (typeof durationDays === "number" && Number.isFinite(durationDays)) {
    return clampLoanDays(durationDays);
  }
  const d = String(duration ?? "");
  if (d in DURATION_DAYS) return DURATION_DAYS[d];
  const m = d.match(/^(\d+)d$/i);
  if (m) return clampLoanDays(Number(m[1]));
  const n = Number(d);
  if (Number.isFinite(n) && n > 0) return clampLoanDays(n);
  return MAX_LOAN_DAYS;
}

export function graceEndIso(dueDate: string, graceDays = GRACE_DAYS): string {
  const t = new Date(dueDate).getTime() + graceDays * 86400000;
  return new Date(t).toISOString();
}

export function loanGraceDays(loan: Pick<Loan, "graceDays" | "verificationLevel">): number {
  if (typeof loan.graceDays === "number" && Number.isFinite(loan.graceDays) && loan.graceDays >= 0) {
    return loan.graceDays;
  }
  return graceDaysForLevel(loan.verificationLevel);
}

export function isInGrace(
  loan: Pick<Loan, "status" | "dueDate" | "graceDays" | "verificationLevel" | "graceEndsAt">,
  now = Date.now(),
): boolean {
  if (loan.status !== "active" && loan.status !== "overdue") return false;
  if (loan.graceEndsAt) {
    const due = new Date(loan.dueDate).getTime();
    const graceEnd = new Date(loan.graceEndsAt).getTime();
    return now > due && now <= graceEnd;
  }
  const due = new Date(loan.dueDate).getTime();
  const graceEnd = due + loanGraceDays(loan) * 86400000;
  return now > due && now <= graceEnd;
}

export function isCarryoverEligible(
  loan: Pick<Loan, "status" | "dueDate" | "graceDays" | "verificationLevel" | "graceEndsAt" | "rolloverCount">,
  now = Date.now(),
): boolean {
  if (loan.status !== "active" && loan.status !== "overdue") return false;
  const max = maxRolloversForLevel(loan.verificationLevel);
  const used = loan.rolloverCount ?? 0;
  if (max === 0) return false;
  if (max != null && used >= max) return false;
  // King: rollover available from due date (grace is late cushion, not the unlock)
  const due = new Date(loan.dueDate).getTime();
  return now >= due;
}

export function canRequestRollover(
  loan: Pick<Loan, "verificationLevel" | "rolloverCount">,
): { ok: boolean; reason?: string } {
  const max = maxRolloversForLevel(loan.verificationLevel);
  const used = loan.rolloverCount ?? 0;
  if (max === 0) {
    return { ok: false, reason: "Rollover is not available on Express loans." };
  }
  if (max != null && used >= max) {
    return { ok: false, reason: `This loan has already used its maximum of ${max} rollover(s).` };
  }
  return { ok: true };
}

/**
 * Remaining balance becomes new capital. Interest for the new tenure:
 * - Express: weekly Express bands only
 * - Verified: loan interest rate (no Express bands; no negotiate on rollover)
 * - Elite: may negotiate total repay; otherwise loan interest rate
 */
export function buildRolloverQuote(
  loan: Loan,
  opts: {
    durationDays?: number;
    /** Elite negotiated total repay for the new tenure (must be >= remaining). */
    proposedRepay?: number;
    expressSchedule?: { maxDays: number; rate: number }[];
  } = {},
): RolloverQuote {
  const remaining = loanOutstanding(loan);
  const level = loan.verificationLevel;
  const canNegotiate = level === "elite";
  const durationDays = clampLoanDaysForLevel(level, opts.durationDays ?? loan.durationDays);
  const useExpressBands =
    level === "express" && Boolean(opts.expressSchedule && opts.expressSchedule.length > 0);

  let ratePercent: number;
  let newRepayable: number;
  let interestDue: number;

  if (
    canNegotiate &&
    opts.proposedRepay != null &&
    Number.isFinite(opts.proposedRepay) &&
    opts.proposedRepay >= remaining
  ) {
    newRepayable = Math.round(opts.proposedRepay * 100) / 100;
    interestDue = Math.round((newRepayable - remaining) * 100) / 100;
    ratePercent = remaining > 0 ? Number((((interestDue / remaining) * 100).toFixed(2))) : 0;
  } else if (useExpressBands) {
    const sorted = [...opts.expressSchedule!].sort((a, b) => a.maxDays - b.maxDays);
    ratePercent =
      sorted.find((b) => durationDays <= b.maxDays)?.rate ?? sorted[sorted.length - 1]?.rate ?? loan.interestRate;
    newRepayable = Math.round(remaining * (1 + ratePercent / 100) * 100) / 100;
    interestDue = Math.round((newRepayable - remaining) * 100) / 100;
  } else {
    ratePercent = loan.interestRate;
    interestDue = Math.round(remaining * (ratePercent / 100) * 100) / 100;
    newRepayable = Math.round((remaining + interestDue) * 100) / 100;
  }

  return {
    loanId: loan.id,
    remaining,
    durationDays,
    ratePercent,
    interestDue,
    newRepayable,
    canNegotiate,
  };
}
